import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { heroBannerService } from '../../services/hero-banner.service';
import { isCoAdminOrAdmin } from '../../utils/roles';
import './HeroBanner.css';

interface BannerAnnouncement {
  text: string;
  visible: boolean;
  updatedBy: string;
  updatedAt: string;
}

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good Morning';
  if (h < 17) return 'Good Afternoon';
  return 'Good Evening';
};

const HeroBanner: React.FC = () => {
  const { user } = useAuth();
  const [bannerAnn, setBannerAnn] = useState<BannerAnnouncement | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');

  const isAdmin = isCoAdminOrAdmin(user?.role);
  const firstName = user?.fullName?.split(' ')[0] || 'there';

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await heroBannerService.get();
        if (!cancelled) {
          setBannerAnn({
            text: data.text || '',
            visible: data.visible,
            updatedBy: data.updatedBy || '',
            updatedAt: data.updatedAt,
          });
        }
      } catch {
        if (!cancelled) setBannerAnn(null);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const handleSave = async () => {
    const trimmed = editText.trim();
    if (!trimmed) return;
    try {
      const data = await heroBannerService.update({ text: trimmed, visible: true });
      setBannerAnn({
        text: data.text || '',
        visible: data.visible,
        updatedBy: data.updatedBy || '',
        updatedAt: data.updatedAt,
      });
      setIsEditing(false);
    } catch (err: any) {
      const m = err.response?.data?.message;
      alert(Array.isArray(m) ? m.join(' ') : m || err.message || 'Failed to save note');
    }
  };

  const handleToggleVisibility = async () => {
    if (!bannerAnn) return;
    try {
      const data = await heroBannerService.update({ visible: !bannerAnn.visible });
      setBannerAnn({
        text: data.text || '',
        visible: data.visible,
        updatedBy: data.updatedBy || '',
        updatedAt: data.updatedAt,
      });
    } catch (err: any) {
      const m = err.response?.data?.message;
      alert(Array.isArray(m) ? m.join(' ') : m || err.message || 'Failed to update visibility');
    }
  };

  const handleStartEdit = () => {
    setEditText(bannerAnn?.text || '');
    setIsEditing(true);
  };

  const hasNote = !!(bannerAnn?.visible && bannerAnn.text);

  return (
    <div className="hero-banner">
      <video
        className="hero-video-bg"
        src="/translucent-blue-squares-floating-and-shifting-in-.mp4"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        aria-hidden="true"
      />
      <div className="hero-bg-overlay" />

      <div className="hero-content">
        <div className="hero-text-section">
          <h2 className="hero-greeting">
            {getGreeting()}, {firstName} San! 👋
          </h2>
          <p className="hero-subtitle">Here's what's happening at Toyotsu Lanka today</p>

          {hasNote && (
            <div className="hero-note">
              <span className="hero-note-badge">HR</span>
              <span className="hero-note-text">{bannerAnn!.text}</span>
            </div>
          )}

          {isAdmin && (
            <div className="hero-admin-controls">
              {isEditing ? (
                <div className="hero-edit-row">
                  <input
                    className="hero-edit-input"
                    type="text"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    placeholder="Add an HR note..."
                    maxLength={120}
                    onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                    autoFocus
                  />
                  <button type="button" className="hero-edit-save" onClick={handleSave}>Save</button>
                  <button type="button" className="hero-edit-cancel" onClick={() => setIsEditing(false)}>Cancel</button>
                </div>
              ) : (
                <div className="hero-action-row">
                  <button type="button" className="hero-action-btn" onClick={handleStartEdit}>
                    {bannerAnn?.text ? 'Edit note' : '+ Add note'}
                  </button>
                  {bannerAnn?.text && (
                    <button type="button" className="hero-toggle-btn" onClick={handleToggleVisibility}>
                      {bannerAnn.visible ? 'Hide' : 'Show'}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="hero-mascot-section">
          <img
            src="/hero-mascot.png"
            alt=""
            className="hero-mascot-img"
            draggable={false}
          />
        </div>
      </div>
    </div>
  );
};

export default HeroBanner;
